import type {OrganizationPriceSettings} from "@/models/organization/organization-price-settings";
import type { OrganizationDateSettings } from '@/models/organization/organization-date-settings';
import {
    type OrganizationLoadUnloadNotificationSettings,
    DefaultOrganizationLoadUnloadNotificationSettings
} from '@/models/organization/organization-load-unload-notification-settings';
import {
    type OrganizationApeSettings,
    DefaultOrganizationApeSettings
} from '@/models/organization/organization-ape-settings';
import { TimeSelectionMode } from '@/constants/time-selection-mode';
import { DefaultPickUpDropOffTimeRange } from '@/constants/assignment';
import { DefaultTransportStartTime } from '@/constants/default-date-settings';
import { ChassisNumberDisplayMode, PrimaryVehicleIdentifier } from '@/constants/vehicle-identifier';
import { SsoMappingType } from '@/constants/sso-mapping-type';
import type { AssignmentStatus } from '@/constants/assignment-status';

export interface OrganizationSettings {
    id: number;
    ssoMappingType: SsoMappingType;
    graphGroupsConsentGranted: boolean;
    name: string;
    transporterCode?: string;
    defaultPickUpTimeInMinutes: number;
    defaultDropOffTimeInMinutes: number;
    useTransportVehicleStationForTransport: boolean;
    usePtvRoutingFallback: boolean;
    urgencyDayRange: number;
    urgencyEndTime: string;
    primaryVehicleIdentifier: PrimaryVehicleIdentifier;
    chassisNumberDisplayMode: ChassisNumberDisplayMode;
    retourEnabled: boolean;
    clientEditableUntilStatus: AssignmentStatus | null;
    sendWayBillToSenderContactPerson: boolean;
    sendEmptyRideNotificationToSenderContactPerson: boolean;
    emptyRideNotificationBccEmail: string | null;
    workdays: OrganizationWorkdays;
    priceSettings: OrganizationPriceSettings[];
    dateSettings: OrganizationDateSettings;
    loadUnloadNotificationSettings: OrganizationLoadUnloadNotificationSettings;
    apeSettings: OrganizationApeSettings;
}

export interface OrganizationWorkdays {
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
}

export const DefaultOrganizationSettings = () => {
    return {
        id: 0,
        name: '',
        ssoMappingType: SsoMappingType.None,
        graphGroupsConsentGranted: false,
        transporterCode: undefined,
        defaultPickUpTimeInMinutes: 0,
        defaultDropOffTimeInMinutes: 0,
        useTransportVehicleStationForTransport: false,
        usePtvRoutingFallback: false,
        urgencyDayRange: 0,
        urgencyEndTime: '00:00:00',
        primaryVehicleIdentifier: PrimaryVehicleIdentifier.LicensePlate,
        chassisNumberDisplayMode: ChassisNumberDisplayMode.LastSixCharacters,
        retourEnabled: false,
        clientEditableUntilStatus: null,
        sendWayBillToSenderContactPerson: false,
        sendEmptyRideNotificationToSenderContactPerson: false,
        emptyRideNotificationBccEmail: null,
        workdays: {
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false,
            sunday: false
        },
        priceSettings: [],
        dateSettings: {
            id: 0,
            defaultStartDate: 1,
            defaultDateRange: 1,
            timeSelectionMode: TimeSelectionMode.DaysAndTime,
            defaultStartTime: DefaultPickUpDropOffTimeRange[0],
            defaultEndTime: DefaultPickUpDropOffTimeRange[1],
            defaultTransportStartTime: DefaultTransportStartTime,
            allowSeparateLoadUnloadWindows: false,
            windowInputMode: null
        },
        loadUnloadNotificationSettings: DefaultOrganizationLoadUnloadNotificationSettings(),
        apeSettings: DefaultOrganizationApeSettings()
    } as OrganizationSettings
}
